// Network-aware video quality picker.
//
// Returns the best AWS video URL for the current user's connection.
// `film` may have any combo of: video_url, video_url_low, video_url_high.
// Falls through gracefully if some are missing.

const firstNonEmpty = (...urls) => urls.find(u => typeof u === 'string' && u.trim().length > 0) || '';

export function getNetworkProfile() {
  if (typeof navigator === 'undefined') return { tier: 'standard', saveData: false, effectiveType: null };
  const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!c) return { tier: 'standard', saveData: false, effectiveType: null };
  const saveData = !!c.saveData;
  const t = (c.effectiveType || '').toLowerCase();
  let tier = 'standard';
  if (saveData || t === 'slow-2g' || t === '2g' || t === '3g') tier = 'low';
  else if (t === '4g') tier = 'high';
  return { tier, saveData, effectiveType: t || null, downlink: c.downlink || null };
}

/**
 * Pick the best AWS URL for the given film and the user's network.
 * `film` shape: { video_url, video_url_low, video_url_high, youtube_id }
 * `forceTier` (optional): 'low' | 'standard' | 'high' to override the auto-pick.
 */
export function pickVideoQuality(film, forceTier = null) {
  if (!film) return { url: '', quality: null };

  const { tier } = forceTier ? { tier: forceTier } : getNetworkProfile();
  const low = film.video_url_low;
  const std = film.video_url;
  const high = film.video_url_high;

  let order;
  if (tier === 'low') {
    order = [['low', low], ['standard', std], ['high', high]];
  } else if (tier === 'high') {
    order = [['high', high], ['standard', std], ['low', low]];
  } else {
    order = [['standard', std], ['high', high], ['low', low]];
  }

  for (const [quality, url] of order) {
    if (url && url.trim()) return { url, quality };
  }
  // Final fallback
  return { url: firstNonEmpty(std, low, high), quality: 'unknown' };
}

export function listAvailableQualities(film) {
  if (!film) return [];
  const out = [];
  if (film.video_url_low) out.push({ quality: 'low', label: 'Mobile', url: film.video_url_low });
  if (film.video_url) out.push({ quality: 'standard', label: 'Standard', url: film.video_url });
  if (film.video_url_high) out.push({ quality: 'high', label: 'High', url: film.video_url_high });
  return out;
}
